import React, { useEffect, forwardRef } from 'react';
import * as CONSTANTS from '../shared/constants';

const { CANVAS_WIDTH, CANVAS_HEIGHT } = CONSTANTS;

const GameCanvas = forwardRef(({ gameState, canvasScale }, ref) => {
  // Handle canvas resizing with high-DPI support
  useEffect(() => {
    // Only resize when canvas is rendered (playing or gameOver)
    if (gameState !== 'playing' && gameState !== 'gameOver') return;
    
    let resizeTimeout;
    const resizeCanvas = () => {
      const canvas = ref.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      
      // Calculate available space for the game, maintaining aspect ratio
      const availableWidth = window.innerWidth - 40; // Account for margins
      const availableHeight = window.innerHeight - 180; // TopBar (~50px) + PlayerStatsDisplay (~80px optimized) + margins (~50px)
      
      const scaleX = availableWidth / CANVAS_WIDTH;
      const scaleY = availableHeight / CANVAS_HEIGHT;
      const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio
      
      // Calculate CSS display size
      const displayWidth = CANVAS_WIDTH * scale;
      const displayHeight = CANVAS_HEIGHT * scale;
      
      // Only resize if size actually changed significantly
      const currentWidth = parseInt(canvas.style.width) || 0;
      const currentHeight = parseInt(canvas.style.height) || 0;
      if (Math.abs(displayWidth - currentWidth) < 5 && Math.abs(displayHeight - currentHeight) < 5) {
        return;
      }
      
      // Set CSS display size
      canvas.style.width = displayWidth + "px";
      canvas.style.height = displayHeight + "px";
      
      // Set actual render size in pixels (high-DPI)
      canvas.width = displayWidth * dpr;
      canvas.height = displayHeight * dpr;
      
      // Scale context so drawing units match our game coordinates
      const ctx = canvas.getContext('2d');
      ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
    };

    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeCanvas, 16); // ~60fps debounce
    };

    // Initial resize
    resizeCanvas();
    
    window.addEventListener('resize', debouncedResize);
    
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, [gameState, ref]);

  return (
    <canvas
      ref={ref}
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
      style={{ 
        border: '2px solid #444',
        backgroundColor: '#0f5132',
        boxShadow: '0 0 20px rgba(0,0,0,0.5)',
        transform: `scale(${canvasScale})`,
        transformOrigin: 'center',
        maxWidth: '100vw',
        maxHeight: '100vh'
      }}
    />
  );
});

GameCanvas.displayName = 'GameCanvas';

export default GameCanvas;